import {
    AccessSegment,
    ColumnMetadata,
    ColumnTypes,
} from "~/components/DataGrid";
import { Emdash } from "~/components/Punctuation";
import { AssetServiceProvider } from "~/modules/assets/types";
import { ImageLoader } from "~/components/ImageLoader";
import { RWA_COIN_ICON } from "~/consts";
import { AssetColumns } from "~/modules/assets/types";
import { AssetTableRowData } from "../types";
import { CatalogReferenceDataSlugs } from "~/modules/catalog/types";
import { Link } from "~/components/Link";
import { getServiceProviderURL } from "~/routes";
import { AssetUserEvents } from "~/modules/analytics/events/assets";

function ServiceProviderContent(props: {
    serviceProvider: AssetServiceProvider | null;
}) {
    const { serviceProvider } = props;

    if (!serviceProvider) {
        return <Emdash />;
    }

    // Return name and icon
    return (
        <div className="flex items-center gap-2">
            <ImageLoader
                src={serviceProvider.icon_url}
                alt={`${serviceProvider.name} icon`}
                className="h-4 w-4 min-w-4 rounded-full"
                fallbackSrc={RWA_COIN_ICON}
                fallbackClassName="bg-white opacity-50"
            />
            <span className="dg-overflow">{serviceProvider.name}</span>
        </div>
    );
}

function ServiceProviderContentWithLink(props: {
    serviceProvider: AssetServiceProvider | null;
    serviceProviderType:
        | "auditor"
        | "crypto_broker"
        | "crypto_custodian"
        | "fund_administrator"
        | "paying_agent"
        | "sub_advisor"
        | "traditional_broker"
        | "transfer_agent"
        | "traditional_custodian"
        | "exchange_agent";
}) {
    const { serviceProvider, serviceProviderType } = props;

    if (!serviceProvider) {
        return <Emdash />;
    }

    // Return name and icon
    return (
        <Link
            href={getServiceProviderURL({
                serviceProviderSlug: serviceProvider.slug,
            })}
            variant="default"
            className="flex items-center gap-2"
            analyticsLabel={AssetUserEvents.ClickServiceProviderLink}
            analyticsEventProperties={{
                service_provider_id: serviceProvider.slug,
                service_provider_slug: serviceProvider.slug,
                service_provider_type: serviceProviderType,
            }}
        >
            <ImageLoader
                src={serviceProvider.icon_url}
                alt={`${serviceProvider.name} icon`}
                className="h-4 w-4 min-w-4 rounded-full"
                fallbackSrc={RWA_COIN_ICON}
                fallbackClassName="bg-white opacity-50"
            />
            <span className="dg-overflow">{serviceProvider.name}</span>
        </Link>
    );
}

// NOTE - stablecoins + commodities ONLY
export const AUDITOR_COLUMN: ColumnMetadata<AssetTableRowData, AssetColumns> = {
    label: "Auditor",
    slug: AssetColumns.auditorID,
    tooltip: CatalogReferenceDataSlugs.auditor,
    sortsOn: "auditor.name" as AssetColumns,
    type: ColumnTypes.string,
    width: 190,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    render: (item) => {
        if (item.isAsset) {
            return <ServiceProviderContent serviceProvider={item.auditor} />;
        }

        return <Emdash />;
    },
};

export const CRYPTO_BROKER_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    label: "Crypto Broker",
    slug: AssetColumns.cryptoBrokerID,
    tooltip: CatalogReferenceDataSlugs.crypto_broker,
    type: ColumnTypes.string,
    width: 190,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    render: (item) => {
        if (item.isAsset) {
            return (
                <ServiceProviderContent serviceProvider={item.crypto_broker} />
            );
        }

        return <Emdash />;
    },
};

export const CRYPTO_CUSTODIAN_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    label: "Crypto Custodian",
    slug: AssetColumns.cryptoCustodianID,
    tooltip: CatalogReferenceDataSlugs.crypto_custodian,
    type: ColumnTypes.string,
    width: 190,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    render: (item) => {
        if (item.isAsset) {
            return (
                <ServiceProviderContent
                    serviceProvider={item.crypto_custodian}
                />
            );
        }

        return <Emdash />;
    },
};

export const FUND_ADMINISTRATOR_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    label: "Fund Administrator",
    slug: AssetColumns.fundAdministratorID,
    tooltip: CatalogReferenceDataSlugs.fund_administrator,
    type: ColumnTypes.string,
    width: 190,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    render: (item) => {
        if (item.isAsset) {
            return (
                <ServiceProviderContent
                    serviceProvider={item.fund_administrator}
                />
            );
        }

        return <Emdash />;
    },
};

export const PAYING_AGENT_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    label: "Paying Agent",
    slug: AssetColumns.payingAgentID,
    tooltip: CatalogReferenceDataSlugs.paying_agent,
    type: ColumnTypes.string,
    width: 190,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    render: (item) => {
        if (item.isAsset) {
            return (
                <ServiceProviderContent serviceProvider={item.paying_agent} />
            );
        }

        return <Emdash />;
    },
};

export const SUB_ADVISOR_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    label: "Sub-Advisor",
    slug: AssetColumns.subAdvisorID,
    tooltip: CatalogReferenceDataSlugs.sub_advisor,
    type: ColumnTypes.string,
    width: 190,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    render: (item) => {
        if (item.isAsset) {
            return (
                <ServiceProviderContent serviceProvider={item.sub_advisor} />
            );
        }

        return <Emdash />;
    },
};

export const TRADITIONAL_BROKER_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    label: "Traditional Broker",
    slug: AssetColumns.traditionalBrokerID,
    tooltip: CatalogReferenceDataSlugs.traditional_broker,
    type: ColumnTypes.string,
    width: 190,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    render: (item) => {
        if (item.isAsset) {
            return (
                <ServiceProviderContent
                    serviceProvider={item.traditional_broker}
                />
            );
        }

        return <Emdash />;
    },
};

export const TRANSFER_AGENT_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    label: "Transfer Agent",
    slug: AssetColumns.transferAgentID,
    tooltip: CatalogReferenceDataSlugs.transfer_agent,
    type: ColumnTypes.string,
    width: 190,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    render: (item) => {
        if (item.isAsset) {
            return (
                <ServiceProviderContent serviceProvider={item.transfer_agent} />
            );
        }

        return <Emdash />;
    },
};

export const TRADITIONAL_CUSTODIAN_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    label: "Custodian",
    slug: AssetColumns.traditionalCustodianID,
    tooltip: CatalogReferenceDataSlugs.traditional_custodian,
    type: ColumnTypes.string,
    width: 190,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    render: (item) => {
        if (item.isAsset) {
            return (
                <ServiceProviderContent
                    serviceProvider={item.traditional_custodian}
                />
            );
        }

        return <Emdash />;
    },
};

// // // // // //

export const AUDITOR_COLUMN_WITH_LINK: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    label: "Auditor",
    slug: AssetColumns.auditorID,
    tooltip: CatalogReferenceDataSlugs.auditor,
    sortsOn: "auditor.name" as AssetColumns,
    type: ColumnTypes.string,
    width: 190,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    render: (item) => {
        if (item.isAsset) {
            return (
                <ServiceProviderContentWithLink
                    serviceProvider={item.auditor}
                    serviceProviderType="auditor"
                />
            );
        }

        return <Emdash />;
    },
};

export const CRYPTO_BROKER_COLUMN_WITH_LINK: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    label: "Crypto Broker",
    slug: AssetColumns.cryptoBrokerID,
    tooltip: CatalogReferenceDataSlugs.crypto_broker,
    type: ColumnTypes.string,
    width: 190,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    render: (item) => {
        if (item.isAsset) {
            return (
                <ServiceProviderContentWithLink
                    serviceProvider={item.crypto_broker}
                    serviceProviderType="crypto_broker"
                />
            );
        }

        return <Emdash />;
    },
};

export const CRYPTO_CUSTODIAN_COLUMN_WITH_LINK: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    label: "Crypto Custodian",
    slug: AssetColumns.cryptoCustodianID,
    tooltip: CatalogReferenceDataSlugs.crypto_custodian,
    type: ColumnTypes.string,
    width: 190,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    render: (item) => {
        if (item.isAsset) {
            return (
                <ServiceProviderContentWithLink
                    serviceProvider={item.crypto_custodian}
                    serviceProviderType="crypto_custodian"
                />
            );
        }

        return <Emdash />;
    },
};

export const FUND_ADMINISTRATOR_COLUMN_WITH_LINK: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    label: "Fund Administrator",
    slug: AssetColumns.fundAdministratorID,
    tooltip: CatalogReferenceDataSlugs.fund_administrator,
    type: ColumnTypes.string,
    width: 190,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    render: (item) => {
        if (item.isAsset) {
            return (
                <ServiceProviderContentWithLink
                    serviceProvider={item.fund_administrator}
                    serviceProviderType="fund_administrator"
                />
            );
        }

        return <Emdash />;
    },
};

export const PAYING_AGENT_COLUMN_WITH_LINK: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    label: "Paying Agent",
    slug: AssetColumns.payingAgentID,
    tooltip: CatalogReferenceDataSlugs.paying_agent,
    type: ColumnTypes.string,
    width: 190,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    render: (item) => {
        if (item.isAsset) {
            return (
                <ServiceProviderContentWithLink
                    serviceProvider={item.paying_agent}
                    serviceProviderType="paying_agent"
                />
            );
        }

        return <Emdash />;
    },
};

export const SUB_ADVISOR_COLUMN_WITH_LINK: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    label: "Sub-Advisor",
    slug: AssetColumns.subAdvisorID,
    tooltip: CatalogReferenceDataSlugs.sub_advisor,
    type: ColumnTypes.string,
    width: 190,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    render: (item) => {
        if (item.isAsset) {
            return (
                <ServiceProviderContentWithLink
                    serviceProvider={item.sub_advisor}
                    serviceProviderType="sub_advisor"
                />
            );
        }

        return <Emdash />;
    },
};

export const TRADITIONAL_BROKER_COLUMN_WITH_LINK: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    label: "Traditional Broker",
    slug: AssetColumns.traditionalBrokerID,
    tooltip: CatalogReferenceDataSlugs.traditional_broker,
    type: ColumnTypes.string,
    width: 190,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    render: (item) => {
        if (item.isAsset) {
            return (
                <ServiceProviderContentWithLink
                    serviceProvider={item.traditional_broker}
                    serviceProviderType="traditional_broker"
                />
            );
        }

        return <Emdash />;
    },
};

export const TRANSFER_AGENT_COLUMN_WITH_LINK: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    label: "Transfer Agent",
    slug: AssetColumns.transferAgentID,
    tooltip: CatalogReferenceDataSlugs.transfer_agent,
    type: ColumnTypes.string,
    width: 190,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    render: (item) => {
        if (item.isAsset) {
            return (
                <ServiceProviderContentWithLink
                    serviceProvider={item.transfer_agent}
                    serviceProviderType="transfer_agent"
                />
            );
        }

        return <Emdash />;
    },
};

export const TRADITIONAL_CUSTODIAN_COLUMN_WITH_LINK: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    label: "Custodian",
    slug: AssetColumns.traditionalCustodianID,
    tooltip: CatalogReferenceDataSlugs.traditional_custodian,
    type: ColumnTypes.string,
    width: 190,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    render: (item) => {
        if (item.isAsset) {
            return (
                <ServiceProviderContentWithLink
                    serviceProvider={item.traditional_custodian}
                    serviceProviderType="traditional_custodian"
                />
            );
        }

        return <Emdash />;
    },
};

export const EXCHANGE_AGENT_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    label: "Exchange Agent",
    slug: AssetColumns.exchangeAgentID,
    tooltip: CatalogReferenceDataSlugs.exchange_agent,
    type: ColumnTypes.string,
    width: 190,
    access: {
        view: AccessSegment.Preview,
        sort: AccessSegment.Preview,
    },
    render: (item) => {
        if (item.isAsset) {
            return (
                <ServiceProviderContentWithLink
                    serviceProvider={item.exchange_agent}
                    serviceProviderType="exchange_agent"
                />
            );
        }

        return <Emdash />;
    },
};
